import React, { useState, useRef, useEffect, useCallback } from "react";
import { Box, Typography, Modal } from "@mui/material";
import { styled } from "@mui/system";
import { Disclosure, SupportedLanguage } from "./disclosure.types";
import { getDisclosureContent } from "./disclosures.config";
import { AcceptButton } from "./AcceptButton";
import { WarningBox } from "./WarningBox";
import { LanguageSelector } from "./LanguageSelector";

interface DisclosureContentModalProps {
  open: boolean;
  /** Called when modal closes. Reason is 'backdropClick' | 'escapeKeyDown' when user dismisses; 'accept' when user accepted. */
  onClose: (event?: any, reason?: string) => void;
  disclosure: Disclosure | null;
  selectedLanguage: SupportedLanguage;
  onLanguageChange: (language: SupportedLanguage) => void;
  onAccept: (disclosureId: string) => void;
}

const ModalContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isStateDisclosure" && prop !== "hasCategories",
})<{ isStateDisclosure?: boolean; hasCategories?: boolean }>(
  ({ isStateDisclosure, hasCategories }) => {
    // Fixed card size for state disclosures: wider stepper card for categorised docs, narrow tall card for flat docs
    const hasFixedDimensions = Boolean(isStateDisclosure);
    const fixedWidth = hasCategories ? "667px" : "518px";
    const fixedHeight = hasCategories ? "664px" : "722px";

    return {
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      backgroundColor: "#FFFFFF",
      display: "flex",
      flexDirection: "column",
      outline: "none",
      overflow: "hidden",
      padding: "40px",
      alignItems: "center",
      gap: "24px",
      borderRadius: "28px",
      boxShadow: "0 4px 20px 0 rgba(173, 192, 203, 0.30)",
      // Default (mobile)
      width: "95%",
      maxWidth: hasFixedDimensions ? fixedWidth : "520px",
      height: hasFixedDimensions ? "90vh" : "auto",
      maxHeight: "90vh",
      // Tablet (768px+)
      "@media (min-width: 768px)": {
        width: "90%",
        height: hasFixedDimensions ? "85vh" : "auto",
        maxHeight: hasFixedDimensions ? fixedHeight : "90vh",
      },
      // Desktop (1024px+)
      "@media (min-width: 1024px)": {
        width: hasFixedDimensions ? fixedWidth : "90%",
        height: hasFixedDimensions ? fixedHeight : "auto",
        maxHeight: hasFixedDimensions ? fixedHeight : "90vh",
      },
    };
  },
);

const Header = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  position: "relative",
  width: "100%",
  padding: 0,
});

const HeaderTitle = styled(Typography)({
  fontFamily: "Roobert",
  color: "#002035",
  textAlign: "center",
  width: "100%",
  fontSize: "24px",
  fontWeight: 500,
  lineHeight: "28px",
  letterSpacing: "-0.48px",
  fontStyle: "normal",
});

const HeaderRight = styled(Box)({
  display: "flex",
  alignItems: "center",
  position: "absolute",
  right: "0px",
  top: "50%",
  transform: "translateY(-50%)",
});

const ContentArea = styled(Box)({
  flex: 1,
  overflowY: "auto",
  width: "100%",
  alignSelf: "stretch",
  padding: 0,
  "&::-webkit-scrollbar": {
    width: "8px",
  },
  "&::-webkit-scrollbar-track": {
    borderRadius: "4px",
    background: "rgba(200, 200, 200, 0.3)",
  },
  "&::-webkit-scrollbar-thumb": {
    borderRadius: "4px",
    background: "rgba(150, 150, 150, 0.5)",
    "&:hover": {
      background: "rgba(150, 150, 150, 0.7)",
    },
  },
});

const NoticeBox = styled(Box)({
  display: "flex",
  padding: "12px",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "flex-start",
  gap: "8px",
  alignSelf: "stretch",
  borderRadius: "10px",
  background: "#F8F9F9",
  color: "#002035",
});

const NoticeTitle = styled(Typography)({
  fontFamily: "Roobert",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 600,
  lineHeight: "16px",
  color: "#002035",
});

const NoticeText = styled(Typography)({
  fontFamily: "Roobert",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "16px",
  color: "#002035",
});

const ContentText = styled(Typography)({
  fontFamily: "Roobert",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "16px",
  color: "#002035",
  whiteSpace: "pre-wrap",
});

const BulletList = styled("ul")({
  margin: "16px 0",
  paddingLeft: "20px",
});

const BulletItem = styled("li")({
  fontFamily: "Roobert",
  fontSize: "14px",
  lineHeight: "1.7",
  color: "#374151",
  marginBottom: "12px",
  "&::marker": {
    color: "#002035",
  },
});

const Footer = styled(Box)({
  width: "100%",
  alignSelf: "stretch",
  padding: 0,
});

const CategoryTitle = styled(Box)({
  backgroundColor: "#F8F9F9",
  borderRadius: "10px",
  padding: "12px 16px",
  alignSelf: "stretch",
  textAlign: "center",
  "@media (min-width: 768px)": {
    padding: "12px 24px",
  },
});

const CategoryTitleText = styled(Typography)({
  fontFamily: "Roobert",
  fontSize: "18px",
  fontWeight: 600,
  lineHeight: "22px",
  color: "#002035",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  "@media (min-width: 768px)": {
    fontSize: "14px",
  },
});

const CategoryContentWrapper = styled(Box)({
  fontFamily: "Roobert",
  fontSize: "14px",
  lineHeight: "1.7",
  color: "#374151",
});

const SectionHeader = styled(Typography)({
  fontFamily: "Roobert",
  fontSize: "14px",
  fontWeight: 700,
  color: "#002035",
  marginTop: "20px",
  marginBottom: "8px",
  "&:first-of-type": {
    marginTop: 0,
  },
});

const SectionContent = styled(Typography)({
  fontFamily: "Roobert",
  fontSize: "14px",
  lineHeight: "1.7",
  color: "#374151",
  marginBottom: "4px",
});

const BulletPoint = styled(Typography)({
  fontFamily: "Roobert",
  fontSize: "14px",
  lineHeight: "1.7",
  color: "#374151",
  paddingLeft: "16px",
  position: "relative",
  marginBottom: "4px",
  "&::before": {
    content: '"•"',
    position: "absolute",
    left: 0,
  },
});

export const DisclosureContentModal: React.FC<DisclosureContentModalProps> = ({
  open,
  onClose,
  disclosure,
  selectedLanguage,
  onLanguageChange,
  onAccept,
}) => {
  const [currentCategoryIndex, setCurrentCategoryIndex] = useState<number>(0);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // Determine if this disclosure type requires scrolling (all types now require scrolling)
  const requiresScrolling = true;

  // Docs with categories step through them one at a time; flat docs render in a single scroll
  const content = disclosure ? getDisclosureContent(disclosure, selectedLanguage) : null;
  const hasCategories = Boolean(content?.categories && content.categories.length > 0);
  const totalCategories = hasCategories ? content!.categories!.length : 0;
  const isLastCategory = currentCategoryIndex === totalCategories - 1;

  // Check if content is scrollable and if user has scrolled to bottom
  const checkScrollPosition = useCallback(() => {
    if (contentRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
      // Consider scrolled to bottom if within 50px of the bottom
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 50;

      // Also check if content doesn't need scrolling (content fits in view)
      const contentFitsInView = scrollHeight <= clientHeight;

      if (isAtBottom || contentFitsInView) {
        setHasScrolledToBottom(true);
      }
    }
  }, []);

  // Reset scroll state when disclosure changes, modal opens, or category changes
  useEffect(() => {
    if (open && disclosure) {
      setHasScrolledToBottom(false);
      // Scroll content area to top
      if (contentRef.current) {
        contentRef.current.scrollTop = 0;
      }
      // Check after a short delay to allow content to render
      const timer = setTimeout(() => {
        checkScrollPosition();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open, disclosure, selectedLanguage, currentCategoryIndex, checkScrollPosition]);

  // Reset category index when disclosure changes
  useEffect(() => {
    if (disclosure) {
      setCurrentCategoryIndex(0);
    }
  }, [disclosure]);

  if (!disclosure || !content) return null;

  const showLanguageSelector = content.supportsLanguageSelection;

  // Current category for stepped (categorised) disclosures
  const currentCategory = hasCategories ? content.categories![currentCategoryIndex] : null;

  // A disclosure with no visible text (e.g. a doc seeded without its categories or
  // content) must never be acceptable — an empty body auto-passes the scroll check.
  const hasRenderableContent = hasCategories
    ? Boolean(currentCategory?.content?.trim())
    : Boolean(content.content?.trim() || content.bulletPoints?.length);

  // Determine if button should be disabled
  const isButtonDisabled = (requiresScrolling && !hasScrolledToBottom) || !hasRenderableContent;

  // Determine button text
  const getButtonText = () => {
    if (!hasRenderableContent) {
      return content.acceptButtonText;
    }
    if (isButtonDisabled) {
      return "Scroll to accept";
    }
    // Stepped disclosures show special text on the last category
    if (hasCategories && isLastCategory) {
      return "I accept & want to proceed";
    }
    return content.acceptButtonText;
  };

  const handleAccept = () => {
    if (isButtonDisabled) return;

    // Categorised disclosures advance to the next category before accepting
    if (hasCategories) {
      if (!isLastCategory) {
        // Move to next category
        setCurrentCategoryIndex((prev) => prev + 1);
        setHasScrolledToBottom(false);
        // Scroll to top
        if (contentRef.current) {
          contentRef.current.scrollTop = 0;
        }
        return;
      }
    }

    // Accept the disclosure
    onAccept(disclosure.id);
    onClose(undefined, "accept");
  };

  const handleScroll = () => {
    if (!hasScrolledToBottom && requiresScrolling) {
      checkScrollPosition();
    }
  };

  const renderFlatDisclosure = () => (
    <>
      {content.bulletPoints && content.bulletPoints.length > 0 && (
        <BulletList>
          {content.bulletPoints.map((point, index) => (
            <BulletItem key={index}>{point}</BulletItem>
          ))}
        </BulletList>
      )}
      {content.content && <ContentText>{content.content}</ContentText>}
    </>
  );

  // Parse and render formatted category content
  const renderCategoryContent = (contentText: string) => {
    const lines = contentText.split("\n");
    const elements: React.ReactNode[] = [];

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();
      if (!trimmedLine) return;

      // Check if it's a bullet point (starts with • or -)
      if (trimmedLine.startsWith("•") || trimmedLine.startsWith("-")) {
        const bulletText = trimmedLine.replace(/^[•-]\s*/, "");
        elements.push(<BulletPoint key={index}>{bulletText}</BulletPoint>);
      }
      // Check if it's a section header (ends with : or is a title line)
      else if (trimmedLine.endsWith(":") || (index === 0 && !trimmedLine.includes(":"))) {
        elements.push(<SectionHeader key={index}>{trimmedLine}</SectionHeader>);
      }
      // Check if it's a labeled section (contains : in the middle)
      else if (trimmedLine.includes(":") && trimmedLine.indexOf(":") < trimmedLine.length - 1) {
        const colonIndex = trimmedLine.indexOf(":");
        const label = trimmedLine.substring(0, colonIndex + 1);
        const value = trimmedLine.substring(colonIndex + 1).trim();
        elements.push(
          <SectionContent key={index}>
            <strong>{label}</strong> {value}
          </SectionContent>,
        );
      }
      // Regular paragraph
      else {
        elements.push(<SectionContent key={index}>{trimmedLine}</SectionContent>);
      }
    });

    return elements;
  };

  const renderCategorisedDisclosure = () => (
    <>
      {hasCategories && currentCategory && (
        <CategoryContentWrapper>
          {renderCategoryContent(currentCategory.content)}
        </CategoryContentWrapper>
      )}
    </>
  );

  const renderContent = () =>
    hasCategories ? renderCategorisedDisclosure() : renderFlatDisclosure();

  const isTermsOfBusiness = disclosure?.type === "terms-of-business";
  const isStateDisclosure = disclosure?.type === "state-disclosure";

  return (
    <Modal open={open} onClose={(e, reason) => onClose(e, reason)}>
      <ModalContainer isStateDisclosure={isStateDisclosure} hasCategories={hasCategories}>
        {isTermsOfBusiness ? (
          <>
            {/* Title */}
            <HeaderTitle>{content.title}</HeaderTitle>

            {/* Notice Box (Effective Date) */}
            {content.effectiveDate && (
              <NoticeBox>
                <NoticeTitle>Effective Date: {content.effectiveDate}</NoticeTitle>
                {content.noticeText && <NoticeText>{content.noticeText}</NoticeText>}
              </NoticeBox>
            )}

            {/* Content Area */}
            <ContentArea ref={contentRef} onScroll={handleScroll}>
              <ContentText>{content.content}</ContentText>
            </ContentArea>

            {/* Button */}
            <AcceptButton onClick={handleAccept} disabled={isButtonDisabled} fullWidth>
              {getButtonText()}
            </AcceptButton>
          </>
        ) : (
          <>
            <Header>
              <HeaderTitle>{content.title}</HeaderTitle>
              <HeaderRight>
                {showLanguageSelector && (
                  <LanguageSelector
                    selectedLanguage={selectedLanguage}
                    onLanguageChange={onLanguageChange}
                    supportedLanguages={content.supportedLanguages}
                  />
                )}
              </HeaderRight>
            </Header>

            {/* Warning box for flat docs that declare one (outside scrollable area) */}
            {!hasCategories && content.warningText && (
              <WarningBox title={content.warningTitle} boxVariant="warning">
                {content.warningText}
              </WarningBox>
            )}

            {/* Category title for stepped docs (outside scrollable area) */}
            {hasCategories && currentCategory && (
              <CategoryTitle>
                <CategoryTitleText>{currentCategory.title}</CategoryTitleText>
              </CategoryTitle>
            )}

            <ContentArea ref={contentRef} onScroll={handleScroll}>
              {renderContent()}
            </ContentArea>

            <Footer>
              <AcceptButton onClick={handleAccept} disabled={isButtonDisabled} fullWidth>
                {getButtonText()}
              </AcceptButton>
            </Footer>
          </>
        )}
      </ModalContainer>
    </Modal>
  );
};

export default DisclosureContentModal;
